import { useState } from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db8hldbk13ra8mig8rtg';

const VALUES = [
  {
    icon: 'ri-shield-check-line',
    title: '资料清晰',
    desc: '每部作品都整理导演、年份、片长与剧情简介等结构化资料。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '检索高效',
    desc: '支持关键词搜索与类型、年代、国家的组合筛选。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '体验友好',
    desc: '本地收藏、相关推荐与响应式布局，随时随地浏览。',
  },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 北条麻妃电影',
    description:
      '北条麻妃电影是一个面向影迷的影视内容展示平台，专注整理作品资料与行业资讯，本站提供联系方式与内容说明。',
    keywords: '关于我们,北条麻妃电影,影迷平台,联系方式,内容说明',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setFormError('请填写完整的姓名、邮箱与留言内容。');
      setStatus('error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('邮箱格式似乎不正确，请检查后重试。');
      setStatus('error');
      return;
    }
    if (message.length > 500) {
      setFormError('留言内容不能超过 500 个字。');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const success = response.ok && parsed?.code === 'OK' && !isSpam;

      if (success) {
        setStatus('success');
        form.reset();
      } else {
        setFormError(
          serverMsg && !isSpam ? serverMsg : '提交失败，请稍后重试或通过邮箱联系我们。',
        );
        setStatus('error');
      }
    } catch {
      setFormError('网络异常，请稍后重试或通过邮箱联系我们。');
      setStatus('error');
    }
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">关于我们</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            了解本站的定位、内容说明与联系方式。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-foreground-950 md:text-xl">网站介绍</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              北条麻妃电影是一个面向影迷的影视内容展示平台，专注整理北条麻妃的影视作品资料与行业资讯。网站以清晰的结构呈现作品大全、剧情介绍与最新动态，帮助观众更高效地查询与了解相关电影。
            </p>
            <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
              无论你想按类型浏览某一类作品，还是想了解某部电影的导演、片长与剧情脉络，都可以在这里快速找到对应的内容。我们也会持续更新影展、访谈与片场动态等资讯。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {VALUES.map((v, i) => (
                <div
                  key={v.title}
                  className="animate-fade-up rounded-lg border border-background-200 bg-background-50 p-4"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className={`${v.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{v.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-6">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-contacts-book-2-line text-primary-600"></i>
                联系方式
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-foreground-500">
                    <i className="ri-mail-line"></i>
                  </span>
                  邮箱：contact@bh-movie.example
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-foreground-500">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  地址：日本 · 东京
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-foreground-500">
                    <i className="ri-time-line"></i>
                  </span>
                  回复时间：工作日 10:00 - 18:00
                </li>
              </ul>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold text-foreground-950 md:text-xl">法律声明</h2>
              <p className="mt-4 text-sm leading-loose text-foreground-600">
                本站为影迷资讯展示平台，所有作品资料、剧情介绍与资讯内容均为演示样例，海报为示意图片，仅用于内容展示与学习交流，不构成任何商业宣传。站内内容遵循相关法律法规，如涉及版权问题请通过上方邮箱与我们联系。
              </p>
            </div>
          </div>

          <aside className="w-full lg:w-96 lg:shrink-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-chat-3-line text-accent-600"></i>
                给我们留言
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                如果你有内容建议、合作意向或纠错反馈，欢迎通过下方表单联系我们。
              </p>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="mt-5 flex flex-col gap-4"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-field-website_alt"
                />

                <div>
                  <label htmlFor="contact-name" className="text-xs font-medium text-foreground-600">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="text-xs font-medium text-foreground-600">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-subject"
                    className="text-xs font-medium text-foreground-600"
                  >
                    留言类型
                  </label>
                  <div className="relative mt-1.5">
                    <select
                      id="contact-subject"
                      name="subject"
                      className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="纠错反馈">纠错反馈</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他">其他</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="text-xs font-medium text-foreground-600"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的想法或问题（不超过 500 字）"
                    className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-base"></i>
                      提交留言
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-xs text-secondary-900">
                    <i className="ri-checkbox-circle-fill"></i>
                    留言已提交，感谢你的反馈，我们会尽快回复。
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs text-accent-800">
                    <i className="ri-error-warning-fill"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}